// Daily challenge: one short networking question per day (same question for everyone on the same
// local date). Presentation and habit only — it gives no XP (XP comes only from src/core/xp.js) and
// unlocks nothing; a solved day counts as activity for the Home "Your week" day streak.
// Stored in the learner store under `daily`: { 'YYYY-MM-DD': { done, firstTry, tries, at } }.
export const DAILY_KEY = 'daily';
const KEEP_DAYS = 70;

/** Local calendar date as YYYY-MM-DD. */
export function dayKey(t = Date.now()) {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Deterministic PRNG (mulberry32) seeded from the date. */
function rng(seedText) {
  let a = 2166136261;
  for (const ch of seedText) a = Math.imul(a ^ ch.charCodeAt(0), 16777619);
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const pickInt = (r, lo, hi) => lo + Math.floor(r() * (hi - lo + 1));
const shuffle = (r, arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
const ip = (n) => [24, 16, 8, 0].map((s) => (n >>> s) & 255).join('.');
const maskOf = (p) => (p === 0 ? 0 : (0xFFFFFFFF << (32 - p)) >>> 0);

const PORTS = [
  ['HTTPS', 'TCP 443'], ['HTTP', 'TCP 80'], ['SSH', 'TCP 22'], ['Telnet', 'TCP 23'], ['DNS', 'UDP 53'],
  ['DHCP (server)', 'UDP 67'], ['SNMP', 'UDP 161'], ['Syslog', 'UDP 514'], ['NTP', 'UDP 123'], ['RDP', 'TCP 3389'], ['FTP (control)', 'TCP 21'], ['SMTP', 'TCP 25'],
];
const COMMANDS = [
  ['shows this PC’s IP address, subnet mask and default gateway', 'ipconfig'],
  ['asks the DHCP server for a new address', 'ipconfig /renew'],
  ['tests whether another device answers (ICMP echo)', 'ping'],
  ['lists every router hop on the way to a destination', 'tracert'],
  ['asks a DNS server to resolve a name', 'nslookup'],
  ['shows a quick up/down summary of every router interface', 'show ip interface brief'],
  ['shows the router’s routing table', 'show ip route'],
  ['shows which switch ports belong to which VLAN', 'show vlan brief'],
];

/** The challenge for a date: { id, kind, prompt, options: [4 strings], answer: index, explain }. */
export function dailyChallenge(key = dayKey()) {
  const r = rng(`routezero-daily-${key}`);
  const kinds = ['subnet', 'port', 'mask', 'hosts', 'command'];
  const kind = kinds[Math.floor(r() * kinds.length)];
  let prompt; let correct; let pool = []; let explain;
  if (kind === 'subnet') {
    const prefix = pickInt(r, 25, 29);
    const host = ((10 << 24) | (pickInt(r, 0, 31) << 16) | (pickInt(r, 0, 255) << 8) | pickInt(r, 1, 254)) >>> 0;
    const size = 2 ** (32 - prefix);
    const net = (host & maskOf(prefix)) >>> 0;
    prompt = `Which network is ${ip(host)}/${prefix} in?`;
    correct = `${ip(net)}/${prefix}`;
    pool = [net + size, net - size, (host & maskOf(24)) >>> 0, net + 2 * size, net - 2 * size, net + 3 * size].filter((n) => n >= 0x0A000000 && n < 0x0B000000).map((n) => `${ip(n >>> 0)}/${prefix}`);
    explain = `A /${prefix} block holds ${size} addresses, so networks start every ${size} in the last octet. ${ip(host)} falls in the block that starts at ${ip(net)}.`;
  } else if (kind === 'port') {
    const order = shuffle(r, PORTS);
    const [svc, port] = order[0];
    prompt = `Which port does ${svc} use by default?`;
    correct = port; pool = order.slice(1).map((x) => x[1]);
    explain = `${svc} uses ${port}. Knowing well-known ports helps you read ACLs and firewall rules quickly.`;
  } else if (kind === 'mask') {
    const prefix = pickInt(r, 17, 30);
    prompt = `Which subnet mask is the same as /${prefix}?`;
    correct = ip(maskOf(prefix));
    pool = [prefix - 1, prefix + 1, prefix - 2, prefix + 2, prefix - 8].filter((q) => q >= 8 && q <= 30).map((q) => ip(maskOf(q)));
    explain = `/${prefix} means ${prefix} network bits set to 1: ${correct}.`;
  } else if (kind === 'hosts') {
    const prefix = pickInt(r, 22, 30);
    const usable = 2 ** (32 - prefix) - 2;
    prompt = `How many usable host addresses are in a /${prefix} subnet?`;
    correct = String(usable);
    pool = [String(usable + 2), String(2 ** (33 - prefix) - 2), String(2 ** (31 - prefix) - 2), String(usable + 1), String(2 ** (34 - prefix) - 2)];
    explain = `2^(32−${prefix}) = ${usable + 2} addresses, minus the network and broadcast addresses = ${usable} usable hosts.`;
  } else {
    const order = shuffle(r, COMMANDS);
    const [what, cmd] = order[0];
    prompt = `Which command ${what}?`;
    correct = cmd; pool = order.slice(1).map((x) => x[1]);
    explain = `${cmd} ${what}.`;
  }
  const opts = [correct, ...[...new Set(pool)].filter((x) => x !== correct && x !== '0').slice(0, 3)];
  const options = shuffle(r, opts.slice(0, 4));
  return { id: key, kind, prompt, options, answer: options.indexOf(correct), explain };
}

/** Today's stored result, and helpers to record an attempt (pure over a plain object). */
export function recordAttempt(data, key, correct, at = Date.now()) {
  const all = { ...(data && typeof data === 'object' ? data : {}) };
  const prev = all[key] || { done: false, tries: 0 };
  if (prev.done) return all;
  const tries = (Number(prev.tries) || 0) + 1;
  all[key] = { done: !!correct, firstTry: !!correct && tries === 1, tries, at: correct ? at : prev.at || null };
  // keep the record small
  const keys = Object.keys(all).sort();
  for (const k of keys.slice(0, Math.max(0, keys.length - KEEP_DAYS))) delete all[k];
  return all;
}

/** Days solved, for the "Your week" streak (activity only, no XP). */
export const solvedDays = (data) => Object.entries(data && typeof data === 'object' ? data : {})
  .filter(([k, v]) => /^\d{4}-\d{2}-\d{2}$/.test(k) && v && v.done && Number.isFinite(v.at)).map(([k, v]) => ({ key: k, at: v.at }));
